import cn from 'classnames';
import React from 'react';
import { useIntl } from 'react-intl';

import { Decision } from '@/shared/api/protocol-ts/model/dto_report_common_pb';
import { useAppDispatch, useMedia } from '@/shared/hooks';
import { Icon } from '@/shared/ui';

import { conditionModel } from '../..';
import { ConditionGroups, ONE_OF_CONDITION_GROUPS } from '../../config';
import { conditionText } from '../../config/i18n';
import {
  CompactCondition,
  ConditionColor,
  ConditionDecisionChange,
} from '../../config/types';

import styles from './ConditionButton.module.scss';

type ConditionButtonProps = {
  className?: string;
  rounded?: boolean;
  disabled?: boolean;
  isChild?: boolean;
  onHoverHandle?: (args?: CompactCondition) => void | undefined;
  showProbability?: boolean;
  conditionInterface?: CompactCondition;
  onClick?: (conditionID: string) => void;
  removeConditionHandle?: (conditionID: string) => void;
  updateConditionDecision?: ConditionDecisionChange;
};

const colorStyle: Record<ConditionColor, string> = {
  yellow: styles.colorYellow,
  red: styles.colorRed,
  purple: styles.colorPurple,
  white: styles.colorWhite,
};

// TODO: 1) Add memoization
// TODO: 2) Simplify props. Why do we need to pass prop one by one when we need the whole ConditionInterface?
const InternalConditionButton: React.ForwardRefRenderFunction<
  HTMLButtonElement,
  ConditionButtonProps
> = (props, ref) => {
  const {
    className,
    rounded = false,
    isChild = false,
    showProbability = true,
    conditionInterface = {} as CompactCondition,
    onClick,
    onHoverHandle,
    updateConditionDecision,
    disabled,
    ...restConditionItemProps
  } = props;

  const { color, probabilityText } = conditionInterface;

  const { isMobile } = useMedia();
  const { formatMessage } = useIntl();
  const dispatch = useAppDispatch();

  const removeDecisionHandle = (
    e: React.MouseEvent<SVGSVGElement, MouseEvent>,
  ) => {
    e.stopPropagation();

    if (typeof updateConditionDecision === 'function') {
      updateConditionDecision({
        conditionIDs: conditionInterface?.ids,
        parentConditionId: conditionInterface?.parentID,
        parentConditionCode: conditionInterface?.parentCode,
        conditionCode: conditionInterface?.code,
        userDecision: Decision.NegativeDecision,
      });
      dispatch(conditionModel.actions.setHoveredConditionIDs([]));
    }
  };

  const hasOnHoverHandle = typeof onHoverHandle === 'function';

  const isOneOfCondition = conditionInterface.group
    ? ONE_OF_CONDITION_GROUPS.includes(
        conditionInterface.group as ConditionGroups,
      )
    : false;

  return (
    <button
      ref={ref}
      {...restConditionItemProps}
      type="button"
      data-component="condition-button"
      data-id={conditionInterface.id}
      onClick={() => {
        if (typeof onClick === 'function') {
          // WARN: This function has specific task: open add condition modal and scroll to the condition toggle element with given ID.
          //       In case of click on child condition, scroll still should be to the parent condition.
          onClick(conditionInterface.parentID ?? conditionInterface.id);
        }
      }}
      onMouseEnter={() => {
        if (hasOnHoverHandle) {
          onHoverHandle(conditionInterface);
        }
      }}
      onMouseLeave={() => {
        if (hasOnHoverHandle) {
          onHoverHandle();
        }
      }}
      className={cn(
        styles.container,
        isChild && styles.isChild,
        isMobile ? 'p3' : 'p2',
        colorStyle[color],
        rounded && styles.rounded,
        !isOneOfCondition && styles.withRemoveButton,
        !hasOnHoverHandle && styles.disableHover,
        className,
      )}
      disabled={disabled}
    >
      <span>
        {conditionText[conditionInterface.code] &&
          formatMessage(conditionText[conditionInterface.code])}
      </span>

      {!!showProbability && !!probabilityText && (
        <div className={styles.probabilityContainer}>
          <Icon name="cat" size={16} />
          <span className="p3">{probabilityText}</span>
        </div>
      )}

      {!isOneOfCondition && (
        <div className={cn(styles.removeIconWrap, colorStyle[color])}>
          <Icon
            name="close"
            size={24}
            className={styles.removeIcon}
            onClick={removeDecisionHandle}
          />
        </div>
      )}
    </button>
  );
};

export const ConditionButton = React.forwardRef<
  HTMLButtonElement,
  ConditionButtonProps
>(InternalConditionButton);
